import { NavigationPage } from '@/app/components'
import { useNavigation } from '@/app/hooks'
import { Card, LoadingAnimation, NavigationList } from '@/components'
import { useAppSelector, useUrlSearchParam } from '@/hooks'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { selectUserHasMadePlanSelection } from '@/selectors'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { useProgramDisabledDevices } from '@/hooks/useProgramDisabledDevices'
import {
  isDeviceTypeAllowedInProgram,
  programsNewCollection,
  ProgramsNewModel,
  selectProgram,
} from '@/reducers/programsNew'
import { match } from 'ts-pattern'
import { useShowEnergySystem } from '@/app/hooks/useShowEnergySystem'
import { useEffect } from 'react'

const devices = [
  {
    id: 'connect-vehicle',
    deviceType: 'vehicle' as DeviceType,
    name: 'Connect vehicle',
    logo: '/dashboard/img/car_models/tesla/model3/Midnight_Silver_Metallic.webp',
    destination: '/connect-device/vehicle',
  },
  {
    id: 'connect-thermostat',
    deviceType: 'thermostat' as DeviceType,
    name: 'Connect thermostat',
    logo: '/img/nest.webp',
    destination: '/connect-device/thermostat',
  },
  {
    id: 'connect-charger',
    deviceType: 'vehicleCharger' as DeviceType,
    name: 'Connect charger',
    logo: '/img/chargepoint-charger.webp',
    destination: '/connect-device/charger/value-prop',
  },
  {
    id: 'energy-system',
    deviceType: 'energySystem' as DeviceType,
    name: 'Connect battery',
    logo: '/img/energy-system.webp',
    destination: '/connect-device/energy-system/connect',
  },
]

function useAvailableDevices() {
  const shouldShowConnectCharger = useFeatureFlag({
    flag: 'connect_charger',
    experimentalValue: true,
  })
  const shouldShowEnergySystem = useShowEnergySystem()
  const { deviceIsDisabled: deviceDisabledByProgram } =
    useProgramDisabledDevices()

  const shouldShowConnectHome = useAppSelector((state) => {
    const home = state.homes.selectedHome
    const hasSelectedUtility = state.utilities.selectedUtility
    const hasUtilityProviderLink = hasSelectedUtility?.has_api_provider_link
    const hasMadeGridSupportSelection =
      state.user.user?.profile.has_made_grid_support_selection
    const hasPlan = selectUserHasMadePlanSelection(state)
    return (
      !home ||
      (hasUtilityProviderLink && !hasMadeGridSupportSelection) ||
      !hasSelectedUtility ||
      !hasPlan
    )
  })

  return devices.filter((device) => {
    switch (device.deviceType) {
      case 'vehicle':
        return !deviceDisabledByProgram('vehicle')
      case 'thermostat':
        if (!shouldShowConnectCharger) {
          return false
        }
        return !deviceDisabledByProgram('thermostat')
      case 'home':
        if (!shouldShowConnectHome) {
          return false
        }
        return !deviceDisabledByProgram('home')
      case 'vehicleCharger':
        return !deviceDisabledByProgram('vehicleCharger')
      case 'energySystem':
        if (!shouldShowEnergySystem) {
          return false
        }
        return !deviceDisabledByProgram('energySystem')
      default:
        return true
    }
  })
}

function isDeviceTypeDisabledForProgram(
  deviceType: DeviceType,
  program: ProgramsNewModel | null | undefined,
): boolean {
  if (!program) return false
  return match(deviceType)
    .with('vehicle', () => !isDeviceTypeAllowedInProgram(program, 'vehicle'))
    .with(
      'thermostat',
      () => !isDeviceTypeAllowedInProgram(program, 'thermostat'),
    )
    .with(
      'vehicleCharger',
      () => !isDeviceTypeAllowedInProgram(program, 'charger'),
    )
    .with('home', () => true)
    .with('energySystem', () => true)
    .exhaustive()
}

function findSoleEnabledDeviceDestination(
  availableDevices: (typeof devices)[number][],
  program: ProgramsNewModel | null | undefined,
): string | null {
  if (!program) return null
  const enabled = availableDevices.filter(
    (d) => !isDeviceTypeDisabledForProgram(d.deviceType, program),
  )
  return enabled.length === 1 ? enabled[0].destination : null
}

const useViewModel = () => {
  const navigation = useNavigation()
  const devicesToShow = useAvailableDevices()
  const { isLoading } = programsNewCollection.useFetch()
  const utilityProgramId = Number(useUrlSearchParam('utilityProgramId'))
  const program = useAppSelector(selectProgram(utilityProgramId))
  const navigateOnClick = (destination: string) => () => {
    if (utilityProgramId) {
      const paramChar = destination.includes('?') ? '&' : '?'
      navigation?.push(
        destination + paramChar + 'utilityProgramId=' + utilityProgramId,
      )
    } else {
      navigation?.push(destination)
    }
  }

  const isDisabled = (deviceType: DeviceType) =>
    isDeviceTypeDisabledForProgram(deviceType, program)

  // Skip this screen entirely when the program restricts enrollment to a single
  // device type — going straight to the brand selection page avoids a
  // one-option click that hurts conversion.
  useEffect(() => {
    if (isLoading || !utilityProgramId) return
    const destination = findSoleEnabledDeviceDestination(devicesToShow, program)
    if (!destination) return
    navigation.replaceRetainingParams(destination)
  }, [isLoading, utilityProgramId, program, devicesToShow, navigation])

  return {
    devicesToShow,
    utilityProgramId,
    isLoading,
    navigateOnClick,
    isDisabled,
  }
}

export default function ConnectDevicePage() {
  const {
    devicesToShow,
    utilityProgramId,
    isLoading,
    navigateOnClick,
    isDisabled,
  } = useViewModel()

  return (
    <NavigationPage id="connect-device-page" title="Connect Device">
      <Card style={{ paddingTop: 0, paddingBottom: 0 }}>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full min-h-40">
            <LoadingAnimation type="falling" centered></LoadingAnimation>
          </div>
        ) : (
          <NavigationList>
            {devicesToShow.map((device, index) => (
              <NavigationList.BasicItem
                variant="image"
                key={`${device.name}-${index}`}
                id={`${device.id}-nav-item`}
                label={device.name}
                imgSrc={device.logo}
                onClick={navigateOnClick(device.destination)}
                disabled={isDisabled(device.deviceType)}
              />
            ))}
          </NavigationList>
        )}
      </Card>
    </NavigationPage>
  )
}
